import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_COLLAPSED_MIN_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH_RATIO,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_EXPANDED_MIN_WIDTH,
  SIDEBAR_EXPANDED_WIDTH_RATIO,
  DEFAULT_VIEWPORT_WIDTH,
  LOGO_SOURCES,
} from './constants';
import { PlaceholderVariant } from './types';

// Sidebar logo preloading
let sidebarLogosPreloaded = false;

export const ensureSidebarLogosPreloaded = () => {
  if (sidebarLogosPreloaded || typeof window === 'undefined' || typeof Image === 'undefined') {
    return;
  }

  sidebarLogosPreloaded = true;
  LOGO_SOURCES.forEach((src) => {
    const img = new Image();
    img.src = src;
    // img.decode() removed — causes "Can't open this page" crash on iOS WebKit
    // after 2-3 page reloads (see PR #571). Setting img.src is sufficient.
  });
};

// Initialize preloading on module load
if (typeof window !== 'undefined') {
  ensureSidebarLogosPreloaded();
}

// Viewport and sidebar width utilities
export const getViewportWidth = (): number => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEWPORT_WIDTH;
  }

  return (
    window.visualViewport?.width ??
    window.innerWidth ??
    document.documentElement?.clientWidth ??
    DEFAULT_VIEWPORT_WIDTH
  );
};

export const getResponsiveSidebarWidth = (collapsed: boolean, viewportWidth: number): number => {
  const ratio = collapsed ? SIDEBAR_COLLAPSED_WIDTH_RATIO : SIDEBAR_EXPANDED_WIDTH_RATIO;
  const minWidth = collapsed ? SIDEBAR_COLLAPSED_MIN_WIDTH : SIDEBAR_EXPANDED_MIN_WIDTH;
  const maxWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const scaledWidth = viewportWidth * ratio;
  return Math.round(Math.min(maxWidth, Math.max(minWidth, scaledWidth)));
};

// File type utilities
export const isPdfFile = (filename: string): boolean => {
  return filename.toLowerCase().endsWith('.pdf');
};

export const isImageFile = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return (
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.bmp') ||
    lower.endsWith('.webp') ||
    lower.endsWith('.tiff') ||
    lower.endsWith('.svg')
  );
};

export const getTextPlaceholderVariant = (filename: string): 'textCsv' | 'textTxt' | 'textMd' | null => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.csv')) return 'textCsv';
  if (lower.endsWith('.txt')) return 'textTxt';
  if (lower.endsWith('.md')) return 'textMd';
  return null;
};

export const getPlaceholderVariant = (filename: string): PlaceholderVariant => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.docx')) return 'docx';
  if (lower.endsWith('.xlsx')) return 'xlsx';
  if (isPdfFile(filename)) return 'pdf';
  if (isImageFile(filename)) return 'image';
  const textVariant = getTextPlaceholderVariant(filename);
  if (textVariant) return textVariant;
  return 'file';
};

export const isTextLikeFile = (file: File): boolean => {
  const type = file.type?.toLowerCase?.() ?? '';
  if (type === 'text/plain') return true;
  if (type === 'text/markdown') return true;
  if (type === 'text/csv' || type === 'application/csv' || type === 'application/vnd.ms-excel') return true;
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
  if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true;
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.docx') || lowerName.endsWith('.xlsx')) return true;
  return getTextPlaceholderVariant(file.name) !== null;
};

export const isPageCountableFile = (filename: string): boolean => {
  const variant = getPlaceholderVariant(filename);
  return (
    variant === 'pdf' ||
    variant === 'image' ||
    variant === 'textCsv' ||
    variant === 'textTxt' ||
    variant === 'textMd' ||
    variant === 'xlsx'
  );
};

export const shouldShowPageCount = (filename: string, pageCount?: number): boolean => {
  if (!isPageCountableFile(filename) || typeof pageCount !== 'number') {
    return false;
  }

  const variant = getPlaceholderVariant(filename);
  const shouldHideSinglePage = variant === 'xlsx' || variant === 'textTxt' || variant === 'textMd';

  if (shouldHideSinglePage && pageCount <= 1) {
    return false;
  }

  return true;
};

// File key generation
export const getFileKey = (file: File): string => `${file.name}-${file.lastModified}`;

// Page count formatting
export const formatPageCountLabel = (count: number): string => `${count} página${count === 1 ? '' : 's'}`;

// Chat title formatting
export const formatChatTitle = (raw: string): string => {
  if (!raw) return '';
  // Remove most emoji and pictographs
  const noEmoji = raw.replace(/[\u{1F300}-\u{1FAFF}\u{1F900}-\u{1F9FF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '');
  // Collapse whitespace
  let t = noEmoji.replace(/\s+/g, ' ').trim();
  // Remove excessive trailing punctuation
  t = t.replace(/[.,;:!?…—-]+$/u, '').trim();
  // Capitalize first letter, keep the rest as-is
  if (t.length > 0) t = t.charAt(0).toUpperCase() + t.slice(1);
  // Truncate to a clean length
  const limit = 50;
  if (t.length > limit) t = t.slice(0, limit).trimEnd() + '…';
  return t;
};

// Attachment grid size calculation
export const getAttachmentThumbSize = (count: number): number => {
  if (count <= 1) return 160;
  if (count <= 2) return 148;
  if (count <= 4) return 128;
  if (count <= 6) return 108;
  return 92;
};

// Base64 file conversion
export const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// Chat grouping utilities
export const getGroupLabel = (updatedAtOrTitle?: string): string => {
  if (!updatedAtOrTitle) return 'Semanas anteriores';

  let parsed: Date | null = null;
  const iso = new Date(updatedAtOrTitle);

  if (!isNaN(iso.getTime())) {
    parsed = iso;
  } else {
    const m = updatedAtOrTitle.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[\-\s](\d{2}):(\d{2}):(\d{2}))?$/);
    if (m) {
      const [, dd, mm, yyyy, HH = '00', MM = '00', SS = '00'] = m;
      parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(HH), Number(MM), Number(SS));
    }
  }

  if (!parsed || isNaN(parsed.getTime())) return 'Semanas anteriores';

  const now = new Date();
  const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = toMidnight(now);
  const day = toMidnight(parsed);
  const diffMs = today.getTime() - day.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays <= 7) return 'Essa semana';
  if (diffDays <= 14) return 'Semana passada';
  return 'Semanas anteriores';
};

export const GROUP_ORDER = ['Hoje', 'Ontem', 'Essa semana', 'Semana passada', 'Semanas anteriores'];

/**
 * Append a freshly-fetched page of chats to the existing sidebar list without
 * introducing duplicate chat_ids.
 *
 * `/api/chat/list` paginates by raw position in the user's `chats` array but
 * filters out empty chats *after* slicing, so a page often returns fewer items
 * than the requested `limit`. `fetchMoreChats` advances the next request's
 * `offset` by the number of chats it actually received, which lags the raw
 * positions the server already consumed — so consecutive pages overlap at the
 * boundary. Blindly concatenating them (`[...userChats, ...data.chats]`) put
 * the boundary chat in the list twice under one chat_id, which rendered a
 * duplicate row and made "delete one" delete both. Keep the first occurrence
 * and append only chat_ids not already present.
 */
export const mergeUniqueChats = (existing: any[], incoming: any[]): any[] => {
  const merged = Array.isArray(existing) ? [...existing] : [];
  const seen = new Set(merged.map((c) => c?.chat_id).filter(Boolean));
  (Array.isArray(incoming) ? incoming : []).forEach((chat) => {
    const id = chat?.chat_id;
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    merged.push(chat);
  });
  return merged;
};

export interface GroupedChatItem {
  type: 'header' | 'chat';
  label?: string;
  chat?: any;
}

export const buildGroupedItems = (chats: any[]): GroupedChatItem[] => {
  const groups: Record<string, any[]> = {
    Hoje: [],
    Ontem: [],
    'Essa semana': [],
    'Semana passada': [],
    'Semanas anteriores': [],
  };

  chats.forEach((c) => {
    const label = getGroupLabel(c?.updated_at || c?.title);
    if (!groups[label]) groups[label] = [];
    groups[label].push(c);
  });

  const items: GroupedChatItem[] = [];
  GROUP_ORDER.forEach((label) => {
    const arr = groups[label] || [];
    if (arr.length > 0) {
      items.push({ type: 'header', label });
      arr.forEach((chat) => items.push({ type: 'chat', chat }));
    }
  });

  return items;
};

// Latex preprocessing
export const preprocessingLatex = (text: string): string => {
  return text
    .replace(/\\\\\[/g, '$$$$')
    .replace(/\\\\\]/g, '$$$$')
    .replace(/\\\\\(/g, '$$$$')
    .replace(/\\\\\)/g, '$$$$')
    .replace(/\\\[/g, '$$$$')
    .replace(/\\\]/g, '$$$$')
    .replace(/\\\(/g, '$$$$')
    .replace(/\\\)/g, '$$$$');
};
